class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.prependQuery = this.dataset.prependQuery;
    this.layout = this.dataset.layout;
    this.resourcesType = this.dataset.resourcesType;

    this.setupEventListeners();
    this.stripBrandQueryFromInput();

    // Store initial content in cache during initialization
    const initialContent = this.querySelector('#predictive-search-results-groups-wrapper')?.innerHTML;
    if (initialContent) {
      this.cachedResults['_initial'] = initialContent;
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    //this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getBrandQuery(searchTerm = this.getQuery()) {
    const term = searchTerm.trim();
    if (!term) return '';
    const prependQuery = this.prependQuery ? `${this.prependQuery} ` : '';
    return `${prependQuery}${term}`;
  }

  stripBrandQuery(searchTerm) {
    const term = searchTerm.trim();
    if (!term || !this.prependQuery) return term;

    const prefix = `${this.prependQuery} `;
    if (term === this.prependQuery) return '';
    if (term.startsWith(prefix)) return term.slice(prefix.length).trim();
    return term;
  }

  stripBrandQueryFromInput() {
    if (!this.prependQuery || !this.input) return;

    const displayQuery = this.stripBrandQuery(this.getQuery());
    if (displayQuery !== this.getQuery()) {
      this.input.value = displayQuery;
      this.searchTerm = displayQuery;
    }
  }

  onChange() {
    //console.log('onChange')
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      //console.log('remove old results')
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    if (this.prependQuery) {
      this.input.value = this.getBrandQuery();
    }

    if (this.layout) {
      const form = this.input.form;
      let viewInput = form.querySelector('input[name="view"]');

      if (!viewInput) {
        viewInput = document.createElement('input');
        viewInput.type = 'hidden';
        viewInput.name = 'view';
        form.appendChild(viewInput);
      }

      viewInput.value = this.layout;
    }
  }

  onFormReset(event) {
   //.log('onFormReset')
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.clearResults();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    //console.log('onFocusOut()')
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    //console.log('onKeyup()')
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    //console.log('onKeydown()')
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      // Get matches safely
      const matches = currentButtonText.match(new RegExp(previousTerm, 'gi'));
      // Check if matches exist and there's more than one occurrence
      if (matches?.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      //console.log(`remove ${this.prependQuery} from ${newButtonText}`)
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__search-for-button")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    //Build final query with the option to append vendor titles or a search tag!
    //based on the current layout
    //in order to search both r+c and play on defualt, let's use a tag system
    const brandQuery = encodeURIComponent(this.getBrandQuery(searchTerm));
    const termQuery = encodeURIComponent(searchTerm);

    const searchForType = this.resourcesType ? this.resourcesType : `query,collection,product`;

    const config = {
      resourcesType: searchForType,
      resourcesLimit: '10',
      resourcesLimitScope: 'each',
      resourcesOptionsUnavailableProducts: 'last',
      resourcesOptionsFields: 'vendor,product_type,body,tag,title,variants.title' 
    }

    // Helper to build API parameters
    const buildApiParams = (query) => 
      `q=${query}&resources[type]=${config.resourcesType}&resources[limit]=${config.resourcesLimit}&resources[limit_scope]=${config.resourcesLimitScope}&resources[options][unavailable_products]=${config.resourcesOptionsUnavailableProducts}&resources[options][fields]=${config.resourcesOptionsFields}&section_id=predictive-search`;

    // Initial fetch with brandQuery
    const performFetch = (apiQuery) => {
      return fetch(
        `${routes.predictive_search_url}?${apiQuery}`,
        { signal: this.abortController.signal }
      )
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const parsedDom = new DOMParser().parseFromString(text, 'text/html');
          const resultsMarkup = parsedDom.querySelector('#shopify-section-predictive-search').innerHTML;
          return { resultsMarkup, parsedDom };
        });
    };

    performFetch(buildApiParams(brandQuery))
      .then(({ resultsMarkup, parsedDom }) => {
        // Check for product results (adjust selector based on your template)
        const productResults = parsedDom.querySelector('[data-predictive-search-product-results]');
        
        // If no products, try with termQuery
        if (!productResults || productResults.childElementCount === 0) {
          return performFetch(buildApiParams(termQuery));
        }
        
        // Cache and render initial results
        this.allPredictiveSearchInstances.forEach(instance => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        return null; // Return null to indicate no secondary fetch
      })
      .then((secondaryResults) => {
        if (!secondaryResults) return; // Exit if no secondary fetch
        
        // Handle secondary results
        const { resultsMarkup, parsedDom } = secondaryResults;
        this.allPredictiveSearchInstances.forEach(instance => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return; // AbortController error
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.inert = false;
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) { 
      this.input.value = '';
      this.removeAttribute('results');
      if (this.cachedResults['_initial']) {
        this.predictiveSearchResults.innerHTML = this.cachedResults['_initial'];
      }
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.inert = true;
    }
  }

  clearResults() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      //console.log('remove old results')
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
